import type {ReactElement} from 'react';
import React, {useCallback, useEffect, useRef} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {getCurrentPageDuration, getCurrentPageTime} from '@Components/poster-editor/poster-editor-reducer';
import {useTimelineWidthPerSecond} from '../../hooks/timeline-hooks';
import {useTimelineScrollContainerRef, useTimelineSeekIndicatorRef} from '../../hooks/useTimelineRefs';
import {TIMELINE_HORIZONTAL_PADDING} from '../../timeline.types';
import {setSeekbarDragging} from '../../timeline-reducer';
import {getAssetUrl} from '@Utils/s3.util';
import styles from './seek-indicator.module.scss';
import store from '@/store';

export function TimelineSeekIndicator(): ReactElement {
  const containerRef = useTimelineScrollContainerRef();
  const indicatorRef = useTimelineSeekIndicatorRef();
  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const timelineHeight = useAppSelector((state) => state.timeline.timelineHeight);
  const widthPerSecond = useTimelineWidthPerSecond();
  const dispatch = useAppDispatch();
  const isDraggingRef = useRef(false);

  const updatePosition = useCallback((): void => {
    if (!indicatorRef.current) {
      return;
    }
    const currentTime = getCurrentPageTime(store.getState());
    const position = Math.max(0, currentTime) * widthPerSecond;
    indicatorRef.current.style.transform = `translate(calc(${position}px - 50%), 0px)`;
  }, [widthPerSecond, indicatorRef]);

  useEffect(() => {
    updatePosition();
    return store.subscribe(updatePosition);
  }, [updatePosition]);

  const calculateTimeFromMousePosition = (clientX: number): number | undefined => {
    if (!containerRef.current || currentPageDuration === undefined) {
      return undefined;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const scrollLeft = containerRef.current.scrollLeft;
    const relativeX = clientX - rect.left + scrollLeft - TIMELINE_HORIZONTAL_PADDING;
    const time = relativeX / widthPerSecond;
    return Math.max(0, Math.min(time, currentPageDuration));
  };

  const handleDragStart = (e: React.PointerEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
    dispatch(setSeekbarDragging(true));

    const handlePointerMove = (moveEvent: PointerEvent): void => {
      if (!isDraggingRef.current) {
        return;
      }
      const relativeTime = calculateTimeFromMousePosition(moveEvent.clientX);
      if (relativeTime !== undefined) {
        void window.posterEditor?.whiteboard?.getCurrentPage().seek(relativeTime);
      }
    };

    const handlePointerUp = (): void => {
      isDraggingRef.current = false;
      dispatch(setSeekbarDragging(false));
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  };

  return (
    <div ref={indicatorRef} className={styles.seekIndicator}>
      <img className={styles.triangle} onPointerDown={handleDragStart} src={getAssetUrl('seek-indicator-triangle.svg')} width="13" height="9" alt="" draggable={false} />
      <div className={styles.line} style={{height: timelineHeight}} />
    </div>
  );
}
